import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAxios } from '../../contexts/AuthContext';
import { Download, IndianRupee } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { AdminHeader } from '../../components/AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try { const { data } = await authAxios.get(`${API}/admin/stats`); setStats(data); }
    catch (err) { console.error('Failed:', err); }
  };

  const statusColor = (s) => ({ new:'bg-blue-50 text-blue-700', trial:'bg-purple-50 text-purple-700', completed:'bg-green-50 text-green-700', in_progress:'bg-amber-50 text-amber-700', delivered:'bg-[#1C1C1E]/10 text-[#1C1C1E]' }[s] || 'bg-gray-50 text-gray-700');

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {[
              { label: 'Total Clients', value: stats.total_clients },
              { label: 'Total Orders', value: stats.total_orders },
              { label: 'Active Employees', value: stats.active_employees },
              { label: 'Pending Orders', value: stats.pending_orders },
            ].map(({ label, value }) => (
              <Card key={label} className="border-[#E5E5EA] bg-white rounded-2xl">
                <CardContent className="p-4 sm:p-5">
                  <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-[#8E8E93]">{label}</p>
                  <p className="text-2xl sm:text-3xl font-sans font-bold text-[#1C1C1E] mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {stats?.finance_summary && (
          <Card className="border-[#E5E5EA] bg-white rounded-2xl mb-6 sm:mb-8">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-[#1C1C1E]" strokeWidth={1.5} />
                <h3 className="text-base sm:text-lg font-sans font-bold text-[#1C1C1E]" style={{ fontFamily: "'DM Sans', sans-serif" }}>Finance Overview</h3>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-6">
                <div>
                  <p className="text-[10px] sm:text-xs uppercase tracking-wide text-[#8E8E93]">Total Revenue</p>
                  <p className="text-lg sm:text-2xl font-medium text-[#1C1C1E]">
                    {(stats.finance_summary.total_revenue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs uppercase tracking-wide text-[#8E8E93]">Advance</p>
                  <p className="text-lg sm:text-2xl font-medium text-green-700">
                    {(stats.finance_summary.total_advance || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs uppercase tracking-wide text-[#8E8E93]">Balance</p>
                  <p className="text-lg sm:text-2xl font-medium text-red-600">
                    {(stats.finance_summary.total_balance || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {stats?.recent_orders?.length > 0 && (
          <Card className="border-[#E5E5EA] bg-white rounded-2xl mb-6 sm:mb-8">
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-sans font-bold text-[#1C1C1E] mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Recent Orders</h3>
              <div className="space-y-2">
                {stats.recent_orders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-2.5 sm:p-3 bg-[#F5F5F5] rounded-2xl">
                    <div className="min-w-0">
                      <span className="font-medium text-sm text-[#1C1C1E]">#{order.order_number}</span>
                      <span className="text-xs sm:text-sm text-[#8E8E93] ml-2 truncate">{order.client_name}</span>
                      <span className="text-xs text-[#8E8E93] ml-1 hidden sm:inline">&middot; {order.garment_type?.replace('_', ' ')}</span>
                    </div>
                    <Badge className={`${statusColor(order.status)} border-0 text-[10px] capitalize shrink-0`}>{order.status?.replace('_', ' ')}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button data-testid="export-clients-btn" variant="outline" className="border-[#E5E5EA] text-[#1C1C1E] rounded-2xl text-sm h-10"
            onClick={async () => {
              const r = await authAxios.get(`${API}/admin/export/clients`, { responseType: 'blob' });
              const a = document.createElement('a'); a.href = window.URL.createObjectURL(new Blob([r.data])); a.download = 'clients.csv'; a.click();
            }}>
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />Export Clients CSV
          </Button>
          <Button data-testid="export-orders-btn" variant="outline" className="border-[#E5E5EA] text-[#1C1C1E] rounded-2xl text-sm h-10"
            onClick={async () => {
              const r = await authAxios.get(`${API}/admin/export/orders`, { responseType: 'blob' });
              const a = document.createElement('a'); a.href = window.URL.createObjectURL(new Blob([r.data])); a.download = 'orders.csv'; a.click();
            }}>
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />Export Orders CSV
          </Button>
        </div>
      </div>
    </div>
  );
}
